import clsx from 'clsx';
import { Fragment } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import type { RootState } from '../../store';
import { navFor } from './nav';
import { NavBadge } from './NavBadge';
import { UserMenu } from './UserMenu';
import { Wordmark } from './Wordmark';

/** Rail (icons only) from 1024 px, full sidebar from 1280 px. Hidden below 1024: BottomNav takes over. */
export const Sidebar = () => {
    const { t } = useTranslation();
    const role = useSelector((s: RootState) => s.auth.user?.role?.name);
    const restaurantId = useSelector((s: RootState) => s.auth.user?.restaurant?.id);
    return (
        <aside className="hidden w-[4.5rem] shrink-0 flex-col border-r border-hairline bg-surface lg:flex xl:w-[15.5rem]">
            <div className="flex h-14 items-center justify-center px-3 xl:justify-start">
                <span className="xl:hidden">
                    <Wordmark rail />
                </span>
                <span className="hidden xl:inline">
                    <Wordmark />
                </span>
            </div>
            <nav aria-label={t('shell.primary')} className="flex-1 overflow-y-auto">
                <ul className="flex flex-col gap-1 p-3">
                    {navFor(role, restaurantId).map((item) => (
                        <Fragment key={item.id}>
                            {item.separated && <li aria-hidden="true" className="my-2 h-px bg-line" />}
                            <li>
                                <NavLink
                                    to={item.to}
                                    end={item.to === '/'}
                                    title={t(item.labelKey)}
                                    className={({ isActive }) =>
                                        clsx(
                                            'relative flex h-11 items-center gap-3 rounded-control px-3 text-small font-medium transition-colors duration-hover ease-snap',
                                            'justify-center xl:justify-start',
                                            isActive ? 'bg-sunken text-strong' : 'text-muted hover:bg-hover'
                                        )
                                    }
                                >
                                    {({ isActive }) => {
                                        const Icon = isActive ? item.iconActive : item.icon;
                                        return (
                                            <>
                                                {isActive && <span aria-hidden="true" className="absolute -left-3 top-3 h-5 w-[3px] rounded-r bg-accent" />}
                                                <Icon className="size-5 shrink-0" aria-hidden="true" />
                                                <span className="sr-only xl:not-sr-only xl:flex-1">{t(item.labelKey)}</span>
                                                {item.badge && <NavBadge className="hidden xl:inline-flex" />}
                                                {item.badge && <RailDot />}
                                            </>
                                        );
                                    }}
                                </NavLink>
                            </li>
                        </Fragment>
                    ))}
                </ul>
            </nav>
            <div className="hidden border-t border-hairline p-3 xl:block">
                <UserMenu variant="row" />
            </div>
        </aside>
    );
};

/** In the rail the count has no room: a dot with the number for assistive tech. */
const RailDot = () => <NavBadge className="absolute right-1.5 top-1.5 !min-h-4 !min-w-4 !px-1 xl:hidden" />;
